import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import Header from 'src/layouts/Header';
import Footer from 'src/layouts/Footer';
import { useParams, useNavigate } from "react-router-dom";
import { NotificationManager } from 'react-notifications';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'src/api/axios';
import { showModal } from 'src/redux/actions/modalActions';
import LastArticles from 'src/components/section/LastArticles';
import { HiChevronDown } from 'react-icons/hi';
import UserBox from 'src/components/UserBox';
import ImageGallery from "react-image-gallery";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { Link } from 'react-router-dom';
import FavoriteButton from 'src/components/common/FavoriteButton';
import useSEO from 'src/hooks/useSEO';
import NotFound from 'src/pages/NotFound';

import pin from 'src/assets/svg/location.svg';
import eye from 'src/assets/svg/eye.svg';
import heart from 'src/assets/svg/heart.svg';
import time from 'src/assets/svg/time.svg';
import reminder from 'src/assets/svg/reminder.svg';
import edit from 'src/assets/svg/edit.svg';
import refresh from 'src/assets/svg/refresh.svg';
import ReportModal from 'src/components/modals/ReportModal';
import moment from 'moment';
import AdTypeBadge from 'src/components/common/AdTypeBadge';


const Article = () => {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [article, setArticle] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [adOptions, setAdOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [articleNotFound, setArticleNotFound] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const galleryRef = useRef(null);
  const zoomRefs = useRef({});

  const calculateAge = (dateOfBirth, adType, ages) => {
    if (adType === 'wanted') {
      if (!ages?.length) return 'Any age';

      // Check if all options are selected
      if (ages.length === 5) return 'Any age';

      // Sort ages by their minimum value
      const sortOrder = {
        '2-4-months': 1,
        '5-12-months': 2,
        '1-2-years': 3,
        '3-5-years': 4,
        'older-than-5-years': 5
      };
      const sortedAges = [...ages].sort((a, b) => sortOrder[a] - sortOrder[b]);

      // Check for continuous ranges
      const has24Months = sortedAges.includes('2-4-months');
      const has512Months = sortedAges.includes('5-12-months');
      const has12Years = sortedAges.includes('1-2-years');
      const has35Years = sortedAges.includes('3-5-years');

      // Only combine ranges if they're actually adjacent
      if (has24Months && has512Months && has12Years && has35Years) {
        return '2 months to 5 years';
      }
      if (has512Months && has12Years && has35Years) {
        return '5 months to 5 years';
      }
      if (has24Months && has512Months && has12Years) {
        return '2 months to 2 years';
      }
      if (has512Months && has12Years) {
        return '5 months to 2 years';
      }
      if (has24Months && has512Months) {
        return '2-12 months';
      }
      if (has12Years && has35Years) {
        return '1-5 years';
      }

      // For non-continuous combinations, list them separately
      const ageMap = {
        '2-4-months': '2-4 months',
        '5-12-months': '5-12 months',
        '1-2-years': '1-2 years',
        '3-5-years': '3-5 years',
        'older-than-5-years': 'Older than 5 years'
      };

      return sortedAges.map(age => ageMap[age]).join(', ');
    }

    // For non-wanted ads, keep existing logic
    if (!dateOfBirth) return '-';

    const years = moment().diff(moment(dateOfBirth), 'years');
    const months = moment().diff(moment(dateOfBirth), 'months') % 12;
    const days = moment().diff(moment(dateOfBirth), 'days') % 30;

    const parts = [];
    if (years > 0) parts.push(`${years} years,`);
    if (months > 0) parts.push(`${months} months,`);
    if (days > 0) parts.push(`${days} days`);

    return parts.length > 0 ? parts.join(' ') : '-';
  };

  const formatRange = (ranges) => {
    if (ranges.length === 0) return '';
    
    if (ranges.length === 1) {
      const ageMap = {
        '2-4-months': '2-4 months',
        '5-12-months': '5-12 months',
        '1-2-years': '1-2 years',
        '3-5-years': '3-5 years',
        'older-than-5-years': 'Older than 5 years'
      };
      return ageMap[ranges[0]];
    }

    // Combine adjacent ranges
    if (ranges.includes('2-4-months') && ranges.includes('5-12-months')) {
      return '2-12 months';
    }
    if (ranges.includes('1-2-years') && ranges.includes('3-5-years')) {
      return '1-5 years';
    }

    return ranges.map(r => formatRange([r])).join(', ');
  };

  // Generate SEO data from article
  const seoData = useMemo(() => {
    // If still loading, show loading title
    if (loading) {
      return {
        title: 'Loading... | SphynxPlace.com',
        description: 'Loading article...',
        robots: 'noindex, follow',
      };
    }
    
    // If article not found, show 404 title
    if (articleNotFound || !article) {
      return {
        title: '404 | SphynxPlace.com',
        description: 'The article you are looking for could not be found.',
        robots: 'noindex, follow',
      };
    }

    const title = `${article.title} - SphynxPlace.com`;
    let description = article.short_description || article.description || 'View this Sphynx cat listing on SphynxPlace.com';
    // Remove HTML tags and limit length
    description = description.replace(/<[^>]*>/g, '').substring(0, 160);
    const image = article.featured_image_url || '/build/favicon-96x96.png';
    const url = `${window.location.origin}/ad/${article.id}/${article.slug}`;

    // Build keywords
    const keywords = ['Sphynx cat', 'Sphynx'];
    if (article.city) keywords.push(`Sphynx ${article.city}`);
    if (article.country_name) keywords.push(`Sphynx ${article.country_name}`);
    if (article.ad_type) {
      const adTypeFormatted = String(article.ad_type).replace(/-/g, ' ');
      keywords.push(adTypeFormatted);
    }

    // Get currency value (can be object or string)
    const currencyValue = article.currency?.value || article.currency?.cca3 || article.currency || null;

    // Structured data
    const productData = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: article.title,
      description: article.description ? article.description.replace(/<[^>]*>/g, '') : '',
      url: url,
    };

    if (article.featured_image_url) {
      productData.image = article.featured_image_url;
    }

    if (article.price && currencyValue && article.ad_type !== 'for-adoption') {
      productData.offers = {
        '@type': 'Offer',
        price: String(article.price),
        priceCurrency: currencyValue,
        availability: 'https://schema.org/InStock',
        url: url,
      };
    }

    if (article.user) {
      const userName = article.user.profile_name || 
        (article.user.first_name && article.user.last_name 
          ? `${article.user.first_name} ${article.user.last_name}` 
          : null);
      if (userName) {
        productData.seller = {
          '@type': 'Person',
          name: userName,
        };
      }
    }

    if (article.city || article.country_name) {
      productData.areaServed = {
        '@type': 'Place',
      };
      if (article.city) productData.areaServed.addressLocality = article.city;
      if (article.country_name) productData.areaServed.addressCountry = article.country_name;
    }

    const breadcrumbData = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: window.location.origin,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Search',
          item: `${window.location.origin}/search`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.title,
          item: url,
        },
      ],
    };

    const articleMeta = {};
    if (article.user) {
      const userName = article.user.profile_name || 
        (article.user.first_name && article.user.last_name 
          ? `${article.user.first_name} ${article.user.last_name}` 
          : null);
      if (userName) {
        articleMeta.author = userName;
      }
    }

    return {
      title,
      description,
      image,
      url,
      type: 'article',
      keywords: keywords.join(', '),
      robots: 'index, follow',
      structuredData: [productData, breadcrumbData],
      article: {
        ...articleMeta,
        section: 'Sphynx Cats',
        tags: ['Sphynx', 'Cat', 'Adoption'],
      },
    };
  }, [article, loading, articleNotFound]);

  // Apply SEO
  useSEO(seoData);

  useEffect(() => {
    if (article) {
      const galleryImages = [];
      
      if (article.gallery) {
        Object.values(article.gallery).forEach(image => {
          galleryImages.push({
            original: image.original_url,
            thumbnail: image.original_url
          });
        });
      }

      if (article.featured_image_url) {
        galleryImages.unshift({
          original: article.featured_image_url,
          thumbnail: article.featured_image_url
        });
      }

      setGallery(galleryImages);
    }
  }, [article]);

  const handleRefresh = () => {
    dispatch(showModal('CONFIRMATION_MODAL', {
      title: 'Refresh Ad',
      message: 'Are you sure you want to refresh this ad?',
      confirmText: 'Refresh',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          const response = await axios.post(`api/article/${id}/refresh`);
          NotificationManager.success(response.data.message ?? 'Article successfully refreshed');
          setArticle(prevArticle => ({
            ...prevArticle,
            ...response.data.data
          }));
        } catch (error) {
          // Error notification is already handled by axios interceptor
        }
      }
    }));
  }

  const handleFinish = () => {
    dispatch(showModal('CONFIRMATION_MODAL', {
      title: 'Finish Ad',
      message: 'Are you sure you want to finish this ad?',
      confirmText: 'Finish',
      cancelText: 'Cancel',
      confirmButtonClass: 'bg-red-600 hover:bg-red-700',
      onConfirm: async () => {
        try {
          const response = await axios.post(`api/article/${id}/finish`);
          if (response.status === 200) {
            NotificationManager.success(response.data.message ?? 'Article successfully finished');
            setArticle(prevArticle => ({
              ...prevArticle,
              ...response.data.data
            }));
          } else {
            NotificationManager.error(response.data.message ?? 'Failed to finish article');
          }
        } catch (error) {
          NotificationManager.error(error.response?.data?.message ?? 'Failed to finish article');
        }
      }
    }));
  }

  const handleActivate = async () => {
    try {
      const response = await axios.post(`api/article/${id}/activate`);
      NotificationManager.success(response.data.message ?? 'Article successfully activated');
      setArticle(prevArticle => ({
        ...prevArticle,
        ...response.data.data
      }));
    } catch (error) {
      NotificationManager.error(error.response?.data?.message ?? 'Failed to activate article');
    }
  }

  const handlePublish = () => {
    dispatch(showModal('CONFIRMATION_MODAL', {
      title: 'Publish Ad',
      message: 'Are you sure you want to publish this ad? Your ad will be visible to everyone.',
      confirmText: 'Publish',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          const response = await axios.post(`api/article/${id}/publish`);
          NotificationManager.success(response.data.message ?? 'Article successfully published');
          setArticle(prevArticle => ({
            ...prevArticle,
            ...response.data.data
          }));
        } catch (error) {
          // Notification handled by interceptor or below
          if (error.response?.data?.errors) {
            // If there are validation errors, we might want to redirect to edit page
            NotificationManager.error('Please complete all required fields before publishing.');
            navigate(`/my/ad/${id}/edit`);
          }
        }
      }
    }));
  }

  const getArticleDetails = async () => {
    try {
      setLoading(true);
      setArticleNotFound(false);
      const response = await axios.get('api/article/' + id, { disableNotification: true });
      setArticle(prevArticle => ({
        ...prevArticle,
        ...response.data.data
      }));
    } catch (error) {
      console.error(error);
      // Check if article doesn't exist (404 or similar)
      if (error.response?.status === 404 || error.response?.status === 403) {
        setArticleNotFound(true);
      } else if (!error.response) {
        NotificationManager.error('No server response');
      } else if (error.response?.status === 401) {
        NotificationManager.error('Unauthorized');
      } else {
        NotificationManager.error(error.message);
        if (error.response.data.message) {
          NotificationManager.error(error.response.data.message);
        }
        console.log(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAdOptions = async () => {
    try {
      if (article?.ad_type) {
        const response = await axios.get(`/api/resources/post-ad?ad_type=${article.ad_type}`);
        setAdOptions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching ad options:', error);
    }
  };

  useEffect(() => {
    // Show 404 page if id is missing or empty
    if (!id || id.trim() === '' || id === '/') {
      setArticleNotFound(true);
      setLoading(false);
      return;
    }

    getArticleDetails();
  }, [id]);

  // Osveži podatke o oglasu kada se korisnik prijavi (da se vidi is_favorited status i druge opcije)
  useEffect(() => {
    if (auth.isLoggedIn && article) {
      getArticleDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isLoggedIn]);

  // Učitaj adOptions kada se učita article
  useEffect(() => {
    if (article?.ad_type) {
      fetchAdOptions();
    }
  }, [article?.ad_type]);

  // Redirektuj na tačan slug ako se slug promenio
  useEffect(() => {
    if (article && article.slug && slug && article.slug !== slug) {
      navigate(`/ad/${article.id}/${article.slug}`, { replace: true });
    }
  }, [article, slug, navigate]);

  // Handle fullscreen change
  const handleScreenChange = useCallback((isFullScreen) => {
    setIsFullscreen(isFullScreen);
    // Reset all zoom transforms when entering/exiting fullscreen
    Object.values(zoomRefs.current).forEach(ref => {
      if (ref?.resetTransform) {
        ref.resetTransform();
      }
    });
  }, []);

  // Custom render function for gallery items with zoom support
  const renderItem = useCallback((item) => {
    const imageIndex = gallery.findIndex(g => g.original === item.original);
    
    if (isFullscreen) {
      return (
        <div className="image-gallery-image-wrapper" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <TransformWrapper
            ref={(ref) => { zoomRefs.current[imageIndex] = ref; }}
            initialScale={1}
            minScale={1}
            maxScale={4}
            centerOnInit={true}
            wheel={{ step: 0.1 }}
            pinch={{ step: 5 }}
            doubleClick={{ mode: "toggle", step: 2 }}
            panning={{ velocityDisabled: true }}
          >
            <TransformComponent
              wrapperStyle={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              contentStyle={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <img
                className="image-gallery-image"
                src={item.original}
                alt={item.originalAlt || ''}
                style={{
                  maxHeight: 'calc(100vh - 120px)',
                  maxWidth: '100vw',
                  objectFit: 'contain'
                }}
              />
            </TransformComponent>
          </TransformWrapper>
        </div>
      );
    }

    // Non-fullscreen rendering (default)
    return (
      <div className="image-gallery-image-wrapper">
        <img
          className="image-gallery-image"
          src={item.original}
          alt={item.originalAlt || ''}
        />
      </div>
    );
  }, [isFullscreen, gallery]);

  // Show 404 page if article not found or id is missing
  if (articleNotFound || !id || id.trim() === '' || id === '/') {
    return <NotFound />;
  }

  if (!article || loading) {
    return (
      <>
        <Header />
        <div className='bg-[#F7F7F7] py-[20px] md:py-[35px]'>
          <div className='container mx-auto px-2 flex flex-col md:flex-row gap-5 justify-between'>
            <div className="w-full min-w-0 bg-white rounded-[10px] p-4 md:p-10 shadow-md">
              <div className="animate-pulse">
                {/* Title and favorite button */}
                <div className='flex relative mt-4 mb-3 gap-2 justify-between'>
                  <div className="h-8 md:h-10 bg-gray-200 rounded w-3/4"></div>
                  <div className="hidden md:block h-10 w-10 bg-gray-200 rounded-full"></div>
                </div>

                {/* Desktop: Location, Time, Views, Favorites */}
                <div className='hidden md:flex flex-wrap gap-2 md:gap-0 mb-6'>
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className='h-6 bg-gray-200 rounded w-24 mr-6'></div>
                  ))}
                </div>

                {/* Image gallery placeholder */}
                <div className="h-[300px] md:h-[400px] bg-gray-200 rounded-lg"></div>

                {/* Mobile: Price/Badge section below image */}
                <div className='md:hidden mt-4'>
                  <div className='flex flex-wrap p-4 rounded-lg bg-[#443F4B] mb-3 items-center justify-center gap-2'>
                    <div className="h-7 w-24 bg-gray-500 rounded"></div>
                    <div className="h-7 w-32 bg-gray-500 rounded"></div>
                  </div>
                  {/* Mobile: Stats below price */}
                  <div className='flex flex-wrap gap-x-4 gap-y-2 text-sm mb-4 justify-center px-2 items-center'>
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className='h-4 bg-gray-200 rounded w-16'></div>
                    ))}
                  </div>
                </div>

                {/* Content sections */}
                <div className="mt-6 md:mt-8">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="mb-8">
                      <div className="h-7 md:h-8 bg-gray-200 rounded w-48 mb-4"></div>
                      <div className="space-y-3">
                        {[...Array(4)].map((_, j) => (
                          <div key={j} className="h-4 bg-gray-200 rounded w-full"></div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="w-full md:max-w-[332px]">
              <div className="animate-pulse">
                {/* Desktop: Price/Badge box */}
                <div className='hidden sm:flex flex-wrap p-4 md:p-6 rounded-lg bg-[#443F4B] mb-4 md:mb-6 items-center justify-center gap-2'>
                  <div className="h-7 w-24 bg-gray-500 rounded"></div>
                  <div className="h-7 w-40 bg-gray-500 rounded"></div>
                </div>
                {/* User box */}
                <div className='hidden md:block bg-white border border-[#EBEBEB] rounded-lg p-5'>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-16 w-16 bg-gray-200 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                  <div className="h-10 bg-gray-200 rounded w-full mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <LastArticles />
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      {article ? (
        <>
        <div className='bg-[#F7F7F7] py-[20px] md:py-[35px]'>
          <div className='container mx-auto px-2 flex flex-col md:flex-row gap-5 justify-between'>
            <div className="w-full min-w-0 bg-white rounded-[10px] p-4 md:p-10 shadow-md">
              <div>
                <div>
                  <div className='flex relative mt-4 mb-3 gap-2 justify-between'>
                    <h2 className="text-[24px] md:text-[35px] leading-[30px] md:leading-[45px] text-purplesp-50">
                      {article.title}
                    </h2>

                    {/* FavoriteButton - Desktop only, hidden on mobile */}
                    <div className='hidden md:block'>
                      <FavoriteButton 
                        articleId={article.id}
                        initialIsFavorited={article.is_favorited}
                        onUpdate={setArticle}
                        className='w-10 h-10 min-w-10 rounded-full flex mt-2 md:mt-0'
                      />
                    </div>

                    <div className='absolute top-[-25px] right-0'>
                      {article.is_boosted && <span className="mt-1 md:mt-0 md:ml-2 h-fit rounded-sm bg-[#E6912E] font-bold py-0.5 px-2.5 text-white text-[13px] md:text-[15px] leading-[22px] uppercase">Boosted</span>}
                      {article.status !== 'published' && <span className="mt-1 md:mt-0 md:ml-2 h-fit rounded-sm bg-[#00000070] font-bold py-0.5 px-2.5 text-white text-[13px] md:text-[15px] leading-[22px] uppercase">{article.status}</span>}
                    </div>
                  </div>

                  {/* Desktop: Location, Time, Views, Favorites */}
                  <div className='hidden md:flex flex-wrap gap-2 md:gap-0'>
                    <p className='flex mr-4 md:mr-6'><img src={pin} alt="location" className='h-[18px] my-auto' /> <span className='my-auto ml-1.5 font-bold text-sm md:text-base'>{article.city || article.country_name ? `${article.country_name || ''}${article.city && article.country_name ? ', ' : ''}${article.city || ''}` : 'Not specified'}</span></p>
                    <p className='flex mr-4 md:mr-6'><img src={time} alt="time" className='h-[18px] my-auto' /><span className='my-auto ml-1.5 text-[#0D0D0D] text-sm md:text-base'>{article.refreshed_at}</span></p>
                    <p className='flex mr-4 md:mr-6'><img src={eye} alt="location" className='h-[15px] my-auto' /> <span className='my-auto ml-1.5 text-sm md:text-base'>{article.views}</span></p>
                    <p className='flex mr-4 md:mr-6'><img src={heart} alt="time" className='h-[18px] my-auto' /><span className='my-auto ml-1.5 text-[#0D0D0D] text-sm md:text-base'>{article.favorites_count}</span></p>
                  </div>
                </div>
              </div>

              {gallery && gallery.length > 0 && (
                <div className="mt-4 gallery-container">
                  <ImageGallery
                    ref={galleryRef}
                    items={gallery}
                    showPlayButton={false}
                    showFullscreenButton={true}
                    showNav={gallery.length > 1}
                    showThumbnails={gallery.length > 1}
                    showIndex={gallery.length > 1}
                    thumbnailPosition="bottom"
                    slideDuration={300}
                    slideInterval={3000}
                    infinite={gallery.length > 1}
                    lazyLoad={true}
                    useBrowserFullscreen={false}
                    disableThumbnailScroll={gallery.length <= 10}
                    renderItem={renderItem}
                    onScreenChange={handleScreenChange}
                    onSlide={() => {
                      // Reset zoom when changing slides
                      Object.values(zoomRefs.current).forEach(ref => {
                        if (ref?.resetTransform) {
                          ref.resetTransform();
                        }
                      });
                    }}
                    onClick={() => {
                      if (!isFullscreen) {
                        const fullscreenButton = document.querySelector('.image-gallery-fullscreen-button');
                        if (fullscreenButton) {
                          fullscreenButton.click();
                        }
                      }
                    }}
                  />
                </div>
              )}

              {/* Mobile: Price and statistics below image */}
              <div className='md:hidden mt-4'>
                <div className='flex flex-wrap p-4 rounded-lg bg-[#443F4B] mb-3 items-center justify-center gap-2'>
                  <AdTypeBadge adType={article.ad_type} />
                  {article.ad_type !== 'for-adoption' && article.price_formatted && (
                    <div className="flex items-center gap-2 w-auto justify-center whitespace-nowrap">
                      <span className='text-white text-[20px] leading-[26px]'>{article.ad_type === 'for-stud' ? 'Stud Fee:' : article.ad_type === 'wanted' ? 'Budget:' : 'Price:'}</span>
                      <span className='text-white text-[20px] leading-[26px] font-bold'>{article.price_formatted}</span>
                    </div>
                  )}
                </div>
                
                {/* Mobile: Views, Favorites (clickable), Time in one row */}
                <div className='flex flex-wrap gap-x-4 gap-y-2 text-sm mb-2 justify-center px-2 items-center'>
                  <div className='flex items-center'>
                    <img src={eye} alt="views" className='h-[14px] w-[14px]' />
                    <span className='ml-1.5 text-gray-600'>{article.views}</span>
                  </div>
                  <div className='flex items-center gap-1.5'>
                    <FavoriteButton 
                      articleId={article.id}
                      initialIsFavorited={article.is_favorited}
                      onUpdate={setArticle}
                      className='w-6 h-6 min-w-6 rounded-full flex'
                    />
                    <span className='text-gray-600'>{article.favorites_count}</span>
                  </div>
                  <div className='flex items-center'>
                    <img src={time} alt="time" className='h-[14px] w-[14px]' />
                    <span className='ml-1.5 text-gray-600'>{article.refreshed_at}</span>
                  </div>
                </div>
                {/* Mobile: Location in separate row */}
                <div className='flex justify-center text-sm mb-4 px-2'>
                  <div className='flex items-center'>
                    <img src={pin} alt="location" className='h-[14px] w-[14px]' />
                    <span className='ml-1.5 font-medium text-gray-700'>{article.city || article.country_name ? `${article.country_name || ''}${article.city && article.country_name ? ', ' : ''}${article.city || ''}` : 'Not specified'}</span>
                  </div>
                </div>
              </div>

              <div className='mt-6 md:mt-10 pb-6 border-b-[1px] border-b-[#DEDEDE]'>
                <h3 className='text-[20px] md:text-[24px] leading-[30px] md:leading-[35px] pb-4 md:pb-6'>{article.ad_type === 'wanted' ? 'Health & Documentation Preferences' : 'Cat Health & Documentation'}</h3>
                <div className='flex flex-wrap gap-x-4 gap-y-2'>
                  <div className='flex w-full md:w-[calc(50%-8px)]'>
                    <div className='text-[16px] md:text-[18px] min-w-[60%] font-bold'>Health Checked</div>
                    <div className={`text-[16px] md:text-[18px] min-w-[80px] ${!article.attributes?.['health-and-documentation']?.includes('health-checked') ? 'text-gray-400' : ''}`}>
                      {(() => {
                        const healthDocs = article.attributes?.['health-and-documentation'] || [];
                        return healthDocs.includes('health-checked') ? 'Yes' : 'Not specified';
                      })()}
                    </div>
                  </div>
                  <div className='flex w-full md:w-[calc(50%-8px)]'>
                    <div className='text-[16px] md:text-[18px] min-w-[60%] font-bold'>Microchipped</div>
                    <div className={`text-[16px] md:text-[18px] min-w-[80px] ${!article.attributes?.['health-and-documentation']?.includes('microchipped') ? 'text-gray-400' : ''}`}>
                      {article.attributes?.['health-and-documentation']?.includes('microchipped') ? 'Yes' : 'Not specified'}
                    </div>
                  </div>
                  <div className='flex w-full md:w-[calc(50%-8px)]'>
                    <div className='text-[16px] md:text-[18px] min-w-[60%] font-bold'>Neutered</div>
                    <div className={`text-[16px] md:text-[18px] min-w-[80px] ${!article.attributes?.['health-and-documentation']?.includes('neutered') ? 'text-gray-400' : ''}`}>
                      {article.attributes?.['health-and-documentation']?.includes('neutered') ? 'Yes' : 'Not specified'}
                    </div>
                  </div>
                  <div className='flex w-full md:w-[calc(50%-8px)]'>
                    <div className='text-[16px] md:text-[18px] min-w-[60%] font-bold'>Vaccinated</div>
                    <div className={`text-[16px] md:text-[18px] min-w-[80px] ${!article.attributes?.['health-and-documentation']?.includes('vaccinated') ? 'text-gray-400' : ''}`}>
                      {article.attributes?.['health-and-documentation']?.includes('vaccinated') ? 'Yes' : 'Not specified'}
                    </div>
                  </div>
                  <div className='flex w-full md:w-[calc(50%-8px)]'>
                    <div className='text-[16px] md:text-[18px] min-w-[60%] font-bold'>Worm & Flea Treated</div>
                    <div className={`text-[16px] md:text-[18px] min-w-[80px] ${!article.attributes?.['health-and-documentation']?.includes('is-worm-treated') ? 'text-gray-400' : ''}`}>
                      {article.attributes?.['health-and-documentation']?.includes('is-worm-treated') ? 'Yes' : 'Not specified'}
                    </div>
                  </div>
                  <div className='flex w-full md:w-[calc(50%-8px)]'>
                    <div className='text-[16px] md:text-[18px] min-w-[60%] font-bold'>Pedigree</div>
                    <div className={`text-[16px] md:text-[18px] min-w-[80px] ${!article.attributes?.['pedigree']?.includes('pedigree') ? 'text-gray-400' : ''}`}>
                      {article.attributes?.['pedigree']?.includes('pedigree') ? 'Yes' : 'Not specified'}
                    </div>
                  </div>
                  <div className='flex w-full md:w-[calc(50%-8px)]'>
                    <div className='text-[16px] md:text-[18px] min-w-[60%] font-bold'>Registration</div>
                    <div className={`text-[16px] md:text-[18px] min-w-[80px] ${(() => {
                      const healthDocs = article.attributes?.['health-and-documentation'] || [];
                      return !(healthDocs.includes('gccf') || healthDocs.includes('tica') || healthDocs.includes('fife')) ? 'text-gray-400' : '';
                    })()}`}>
                      {(() => {
                        const healthDocs = article.attributes?.['health-and-documentation'] || [];
                        const registrations = [];
                        
                        if (healthDocs.includes('gccf')) registrations.push('GCCF');
                        if (healthDocs.includes('tica')) registrations.push('TICA');
                        if (healthDocs.includes('fife')) registrations.push('FIFe');
                        
                        return registrations.length > 0 ? registrations.join(', ') : 'Not specified';
                      })()}
                    </div>
                  </div>
                  <div className='flex w-full md:w-[calc(50%-8px)]'>
                    <div className='text-[16px] md:text-[18px] min-w-[60%] font-bold'>Age</div>
                    <div className='text-[16px] md:text-[18px] min-w-[80px]'>
                      {calculateAge(
                        article.attributes?.['date-of-birth'],
                        article.ad_type,
                        article.attributes?.age
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className='mt-6 md:mt-10 pb-6 border-b-[1px] border-b-[#DEDEDE]'>
                <h3 className='text-[20px] md:text-[24px] leading-[30px] md:leading-[35px] pb-4 md:pb-6'>Cat Characteristics</h3>
                <div className='flex flex-col gap-y-2'>
                  <div className='flex flex-col sm:flex-row w-full'>
                    <div className='text-[16px] md:text-[18px] min-w-[60%] md:min-w-[30%] font-bold'>Color</div>
                    <div className={`text-[16px] md:text-[18px] ${!article.attributes?.color?.length ? 'text-gray-400' : ''}`}>
                      {article.attributes?.color?.join(', ') || 'Not specified'}
                    </div>
                  </div>
                  <div className='flex flex-col sm:flex-row w-full'>
                    <div className='text-[16px] md:text-[18px] min-w-[60%] md:min-w-[30%] font-bold'>Gender</div>
                    <div className={`text-[16px] md:text-[18px] ${!(article.attributes?.['litter-male-count'] || article.attributes?.['litter-female-count']) ? 'text-gray-400' : ''}`}>
                      {article.attributes?.['litter-male-count'] || article.attributes?.['litter-female-count']
                        ? [
                            article.attributes['litter-male-count'] > 0 && `${article.attributes['litter-male-count']} ${article.attributes['litter-male-count'] > 1 ? 'Males' : 'Male'}`,
                            article.attributes['litter-female-count'] > 0 && `${article.attributes['litter-female-count']} ${article.attributes['litter-female-count'] > 1 ? 'Females' : 'Female'}`
                          ].filter(Boolean).join(' & ')
                        : 'Not specified'}
                    </div>
                  </div>
                  <div className='flex flex-col sm:flex-row w-full'>
                    <div className='text-[16px] md:text-[18px] min-w-[60%] md:min-w-[30%] font-bold'>Temperament</div>
                    <div className={`text-[16px] md:text-[18px] ${!article.attributes?.temperament?.length ? 'text-gray-400' : ''}`}>
                      {article.attributes?.temperament?.join(', ') || 'Not specified'}
                    </div>
                  </div>
                  <div className='flex flex-col sm:flex-row w-full'>
                    <div className='text-[16px] md:text-[18px] min-w-[60%] md:min-w-[30%] font-bold'>Compatibility</div>
                    <div className={`text-[16px] md:text-[18px] ${!article.attributes?.compatibility?.length ? 'text-gray-400' : ''}`}>
                      {article.attributes?.compatibility?.map(item => {
                        const labels = {
                          'okay-with-cats': 'cats',
                          'okay-with-dogs': 'dogs',
                          'okay-with-kids': 'kids',
                          'okay-with-seniors': 'seniors', 
                          'okay-with-apartments': 'apartments'
                        };
                        return labels[item];
                      }).join(', ') || 'Not specified'}
                    </div>
                  </div>
                  {article.attributes?.['energy-level'] && (
                    <div className='flex flex-col sm:flex-row w-full'>
                      <div className='text-[16px] md:text-[18px] min-w-[60%] md:min-w-[30%] font-bold'>Energy Level</div>
                      <div className='text-[16px] md:text-[18px]'>
                        {adOptions['energy-level']?.options?.[article.attributes['energy-level']] || article.attributes['energy-level']}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Care & Habits sekcija */}
              {(article.attributes?.['home-lifestyle'] || article.attributes?.['litter-habits'] || article.attributes?.['grooming-level']) && (
                <div className='mt-6 md:mt-10 pb-6 border-b-[1px] border-b-[#DEDEDE]'>
                  <h3 className='text-[20px] md:text-[24px] leading-[30px] md:leading-[35px] pb-4 md:pb-6'>Care & Habits</h3>
                  <div className='flex flex-col gap-y-2'>
                    {article.attributes?.['home-lifestyle'] && (
                      <div className='flex flex-col sm:flex-row w-full'>
                        <div className='text-[16px] md:text-[18px] min-w-[60%] md:min-w-[30%] font-bold'>Home Lifestyle</div>
                        <div className='text-[16px] md:text-[18px]'>
                          {adOptions['home-lifestyle']?.options?.[article.attributes['home-lifestyle']] || article.attributes['home-lifestyle']}
                        </div>
                      </div>
                    )}
                    {article.attributes?.['litter-habits'] && (
                      <div className='flex flex-col sm:flex-row w-full'>
                        <div className='text-[16px] md:text-[18px] min-w-[60%] md:min-w-[30%] font-bold'>Litter Habits</div>
                        <div className='text-[16px] md:text-[18px]'>
                          {adOptions['litter-habits']?.options?.[article.attributes['litter-habits']] || article.attributes['litter-habits']}
                        </div>
                      </div>
                    )}
                    {article.attributes?.['grooming-level'] && (
                      <div className='flex flex-col sm:flex-row w-full'>
                        <div className='text-[16px] md:text-[18px] min-w-[60%] md:min-w-[30%] font-bold'>Grooming Level</div>
                        <div className='text-[16px] md:text-[18px]'>
                          {adOptions['grooming-level']?.options?.[article.attributes['grooming-level']] || article.attributes['grooming-level']}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className='mt-6 md:mt-10 pb-6 border-b-[1px] border-b-[#DEDEDE]'>
                <h3 className='text-[20px] md:text-[24px] leading-[30px] md:leading-[35px] pb-4 md:pb-6'>Description</h3>
                <p className='text-sm md:text-base'>{article.description}</p>
              </div>
              {article.ad_type === 'for-stud' && article.attributes?.['breeding-requirements'] && (
                <div className='mt-6 md:mt-10 pb-6 border-b-[1px] border-b-[#DEDEDE]'>
                  <h3 className='text-[20px] md:text-[24px] leading-[30px] md:leading-[35px] pb-4 md:pb-6'>Special Breeding Requirements</h3>
                  <p className='text-sm md:text-base'>{article.attributes['breeding-requirements']}</p>
                </div>
              )}


              <div className='mt-6 md:mt-10 pb-6 border-b-[1px] border-b-[#DEDEDE]'>
                <div className="flex items-start gap-2 p-4 bg-[#FFF7E7] rounded-lg">
                  <div className="text-[#F6A609] text-xl">⚠️</div>
                  <div className="text-sm md:text-base">
                    <span className="font-bold text-[#F6A609] mb-1">Disclaimer: </span>
                    Sphynxplace.com does not guarantee the accuracy of ads, the legitimacy of sellers, or the safety of payments. Buyers are fully responsible for verifying all information and ensuring secure transactions before making any payment.
                  </div>
                </div>
              </div>

              {/* User Profile - Mobile only, shown after Disclaimer */}
              <div className="mt-6 md:mt-10 pb-6 border-b-[1px] border-b-[#DEDEDE] md:hidden">
                <div className='bg-white border border-[#EBEBEB] rounded-lg p-4 md:p-5'>
                  <UserBox user={article.user} copyLinkLabel="Copy Ad Link" />
                  <div className='text-center mt-3'>
                    <ReportModal type="ad" />
                  </div>
                </div>
                
                {/* Edit/Refresh/Finish AD buttons - Mobile only, below User Profile */}
                {article.can_edit && (
                  <>
                    <div className='flex gap-2.5 mt-4'>
                      {article.status === 'published' && (
                        <>
                          <Link to={`/my/ad/${article.id}/edit`} className='w-full flex py-2.5 px-4 rounded-[10px] bg-white border border-[#CBCBCB] justify-center hover:bg-[#832EE61C] transition'>
                            <img src={edit} alt="edit" className='my-auto h-[13px]' />
                            <span className='text-sm my-auto ml-2'>Edit AD</span>
                          </Link>
                          <button onClick={handleRefresh} className='w-full flex py-2.5 px-4 rounded-[10px] bg-white border border-[#CBCBCB] justify-center hover:bg-[#832EE61C] transition'>
                            <img src={refresh} alt="refresh" className='my-auto h-[13px]' />
                            <span className='text-sm my-auto ml-2'>Refresh AD</span>
                          </button>
                        </>
                      )}
                      {article.status === 'draft' && (
                        <>
                          <Link to={`/my/ad/${article.id}/edit`} className='w-full flex py-2.5 px-4 rounded-[10px] bg-white border border-[#CBCBCB] justify-center hover:bg-[#832EE61C] transition'>
                            <img src={edit} alt="edit" className='my-auto h-[13px]' />
                            <span className='text-sm my-auto ml-2'>Complete AD</span>
                          </Link>
                          <button onClick={handlePublish} className='w-full flex py-2.5 px-4 rounded-[10px] bg-[#832EE6] text-white justify-center hover:bg-purple-800 transition'>
                            <span className='text-sm my-auto'>Publish AD</span>
                          </button>
                        </>
                      )}
                    </div>

                    {article.status === 'published' && (
                      <button onClick={handleFinish} className='w-full flex py-2.5 px-4 rounded-[10px] bg-white border border-[#CBCBCB] justify-center hover:bg-[#832EE61C] transition mt-2.5'>
                        <span className='text-sm my-auto ml-2'>Finish AD</span>
                      </button>
                    )}
                    {article.status === 'finished' && (
                      <button onClick={handleActivate} className='w-full flex py-2.5 px-4 rounded-[10px] bg-white border border-[#CBCBCB] justify-center hover:bg-[#832EE61C] transition mt-2.5'>
                        <img src={refresh} alt="refresh" className='my-auto h-[13px]' />
                        <span className='text-sm my-auto ml-2'>Activate AD again</span>
                      </button>
                    )}
                  </>
                )}
              </div>

              <div className="mt-6 md:mt-10 pb-6 border-b-[1px] border-b-[#DEDEDE]">
                <h3 className='text-[20px] md:text-[24px] leading-[30px] md:leading-[35px] flex mb-4'>Important Reminders <img src={reminder} alt="reminder" className='ml-1' /></h3>
                <div className="text-sm md:text-base">
                  <p className="mb-2">Exercise caution with online transactions! <br /> Take the time to review these essential tips before making a decision to buy or adopt a Sphynx cat.</p>

                  <p className="mb-2 font-bold">Have You Researched Thoroughly?</p>
                  <p className="mb-2">Before committing to a purchase, educate yourself about the breed. Owning a cat is a significant responsibility, so make sure you understand its needs, temperament, and whether it fits your lifestyle and budget.</p>

                  <p className="mb-2 font-bold">Have you met the kitten in person?</p>
                  <p className="mb-2">Whenever possible, visit the seller's location to see the kitten and verify the living conditions. Avoid relying only on online messages and photos. If the seller is unwilling to meet in person or continuously avoids providing accurate details, treat this as a red flag. As an alternative, consider requesting a live video call to observe the kitten and its environment.</p>

                  <p className="mb-2 font-bold">Avoid unsecured payments</p>
                  <p className="mb-2">Do not send money through unverified methods, especially without personally inspecting the kitten and meeting the seller. This includes deposits or delivery charges. Always use secure payment methods with buyer protection and ensure you have a signed agreement before proceeding.</p>

                  <p className="mb-2 font-bold">Check the documents and health records</p>
                  <p className="mb-2">Ask for all relevant documentation, such as pedigree certificates (from recognized organizations like TICA, GCCF, or FIFe) and veterinary health records. Ensure that the kitten's vaccinations are up-to-date and request evidence of deworming and flea treatments. Trustworthy sellers will also address health concerns or provide refunds if a vet confirms poor health.</p>

                  <p className="mb-2 font-bold">A responsible seller's commitment</p>
                  <p className="mb-2">Kittens should only be rehomed once they are at least 8 weeks old. Taking them away earlier can lead to health and behavioral problems. Verify that the kitten has been well cared for and has had sufficient time with its mother before adoption.</p>
                </div>
              </div>
            </div>

            <div className="w-full md:max-w-[332px]">
              <div className='hidden sm:flex flex-wrap p-4 md:p-6 rounded-lg bg-[#443F4B] mb-4 md:mb-6 items-center justify-center gap-2'>
                <AdTypeBadge adType={article.ad_type} />
                {article.ad_type !== 'for-adoption' && article.price_formatted && (
                  <div className="flex items-center gap-2 w-full sm:w-auto mt-2 sm:mt-0 justify-center">
                    <span className='text-white text-[20px] md:text-[24px] leading-[26px] md:leading-[30px]'>{article.ad_type === 'for-stud' ? 'Stud Fee:' : article.ad_type === 'wanted' ? 'Budget:' : 'Price:'}</span>
                    <span className='text-white text-[20px] md:text-[24px] leading-[26px] md:leading-[30px] font-bold'>{article.price_formatted}</span>
                  </div>
                )}
              </div>

              {/* User Profile - Desktop only, hidden on mobile */}
              <div className='hidden md:block bg-white border border-[#EBEBEB] rounded-lg p-4 md:p-5'>
                <UserBox user={article.user} copyLinkLabel="Copy Ad Link" />
                <div className='text-center mt-3'>
                  <ReportModal type="ad" />
                </div>
              </div>

              {/* Edit/Refresh/Finish AD buttons - Desktop only */}
              {article.can_edit && (
                <>
                  <div className='hidden md:flex gap-2.5 my-4'>
                    {article.status === 'published' && (
                      <>
                        <Link to={`/my/ad/${article.id}/edit`} className='w-full flex py-2.5 md:py-3 px-4 md:px-5 rounded-[10px] bg-white border border-[#CBCBCB] justify-center hover:bg-[#832EE61C] transition'>
                          <img src={edit} alt="edit" className='my-auto h-[13px]' />
                          <span className='text-sm md:text-base my-auto ml-2'>Edit AD</span>
                        </Link>
                        <button onClick={handleRefresh} className='w-full flex py-2.5 md:py-3 px-4 md:px-5 rounded-[10px] bg-white border border-[#CBCBCB] justify-center hover:bg-[#832EE61C] transition'>
                          <img src={refresh} alt="refresh" className='my-auto h-[13px]' />
                          <span className='text-sm md:text-base my-auto ml-2'>Refresh AD</span>
                        </button>
                      </>
                    )}
                    {article.status === 'draft' && (
                      <>
                        <Link to={`/my/ad/${article.id}/edit`} className='w-full flex py-2.5 md:py-3 px-4 md:px-5 rounded-[10px] bg-white border border-[#CBCBCB] justify-center hover:bg-[#832EE61C] transition'>
                          <img src={edit} alt="edit" className='my-auto h-[13px]' />
                          <span className='text-sm md:text-base my-auto ml-2'>Complete AD</span>
                        </Link>
                        <button onClick={handlePublish} className='w-full flex py-2.5 md:py-3 px-4 md:px-5 rounded-[10px] bg-[#832EE6] text-white justify-center hover:bg-purple-800 transition'>
                          <span className='text-sm md:text-base my-auto'>Publish AD</span>
                        </button>
                      </>
                    )}
                  </div>

                  {article.status === 'published' && (
                    <button onClick={handleFinish} className='hidden md:flex w-full py-2.5 md:py-3 px-4 md:px-5 rounded-[10px] bg-white border border-[#CBCBCB] justify-center hover:bg-[#832EE61C] transition'>
                      <span className='text-sm md:text-base my-auto ml-2'>Finish AD</span>
                    </button>
                  )}
                  {article.status === 'finished' && (
                    <button onClick={handleActivate} className='hidden md:flex w-full py-2.5 md:py-3 px-4 md:px-5 rounded-[10px] bg-white border border-[#CBCBCB] justify-center hover:bg-[#832EE61C] transition'>
                      <img src={refresh} alt="refresh" className='my-auto h-[13px]' />
                      <span className='text-sm md:text-base my-auto ml-2'>Activate AD again</span>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
          </div>
          <LastArticles key={article.id} adType={article.ad_type} excludeIds={[article.id]} />
        </>
      ) : null}

      <Footer />
    </>
  )
}

export default Article
